import { Injectable } from '@angular/core';
import { InAppBrowser, InAppBrowserEvent } from '@ionic-native/in-app-browser/ngx';
import { InAppBrowserObject } from "@ionic-native/in-app-browser/ngx";
import { Subscription } from 'rxjs';
import { ConfigService } from '../config/config.service';
import { TimeService } from '../time-service/time.service';
import { MobileDataService } from '../mobile-data/mobile-data.service';
import { Platform } from '@ionic/angular';
import { EventsService } from '../event/event.service';
import { ScreenOrientation } from "@ionic-native/screen-orientation/ngx";
import { WebservicesService } from '../webservices/webservices.service';
import { formatDate } from '@angular/common';
import { GamesService } from '../lobby/games.service';

@Injectable({
  providedIn: 'root'
})
export class IabControllerService {
  protected windowRef: any;
  public timeOnlineEnabled: boolean;
  public onlineTimeInterval: any;
  private vendorName = "";
  private gameParams: any;
  public netWinOperator: string = "+";
  public netPositionDelay: any;
  public netPositionDisplay: boolean = true;
  public netWinAmount: string = "0.00";
  public netPosition = ["0.00"];
  public dataToDisplay: string;
  private statsDialogDisplayTime: boolean;

  constructor(
    private iab: InAppBrowser,
    private config: ConfigService,
    private timeService: TimeService,
    private mobileData: MobileDataService,
    private platform: Platform,
    private events: EventsService,
    private screenOrientation: ScreenOrientation,
    private webservices: WebservicesService,
    private gameService:GamesService
  ) {
    this.timeOnlineEnabled = this.config.getConfig().FEATURE_SHOW_TIME_ONLINE ?? 0;
    this.netPositionDisplay = this.config.getConfig().FEATURE_SHOW_NET_POSITION ?? 0;
    this.statsDialogDisplayTime = this.config.getConfig().FEATURE_STATS_DIALOG_DISPLAY_TIME || 25000;

    this.events.subscribe("event:authentication:logout", () => {
      this.closeIAB();
    });
    this.events.subscribe("event:close:IAB", () => {
      this.closeIAB();
    });
  }

  private closeIAB() {
    if (this.windowRef && typeof this.windowRef.close === 'function') {
      this.windowRef.close();
      this.windowRef = null;
    }
  }

  launchIAB(gameURL, gamePrms) {
    this.gameParams = gamePrms
    this.vendorName = gamePrms?.vendor_name?.toLowerCase();
    const options: string = "hidden=yes,location=no,footer=no,hardwareback=no,hidenavigationbuttons=yes,closebuttoncaption=<back,allowInlineMediaPlayback=yes";
    var windowRef = this.iab.create(gameURL, "_blank", options);
    this.setWindowRef(windowRef);

    let subscriber: Subscription = windowRef.on("loadstop").subscribe((evt: InAppBrowserEvent) => {
      windowRef.show();
      subscriber.unsubscribe();
      if (typeof gamePrms.session_token !== "undefined") {
        if (this.timeOnlineEnabled) {
          this.setupTimeOnlineOverlay();
        }
      }
      this.handleSessionNetPosition();
      this.events.subscribe("event:sessonstats", (e) => {
        console.log("Session Stats Recieved" + e);
        this.injectSessionStatsPopUpPopover(e);
      });

      this.injectGameEventListeners();
      this.onGameClosed(windowRef, () => {
        clearInterval(this.onlineTimeInterval);
        this.onlineTimeInterval = null;
        this.gameCloseService();
      });

    });
  }

  private injectGameEventListeners() {
    const vendor = this.vendorName?.toLowerCase();
    const vendorHandlerMap: Record<string, Record<string, string>> = {
      igt: {
        rgs2custReloadGame: "window.rgs2custReloadGame = true;",
        rgs2custReloadGameInProgress: "window.rgs2custReloadGameInProgress = true;"
      },
    };
    const messageHandlers = vendorHandlerMap[vendor];
    if (!messageHandlers || Object.keys(messageHandlers).length === 0) {
      return;
    }
    const handlerScriptEntries = Object.entries(messageHandlers)
      .map(([eventName, handlerCode]) => `"${eventName}": function () { ${handlerCode} }`).join(",\n");

    const script = `(function () {
      const messageHandlers = { ${handlerScriptEntries} };
      window.addEventListener("message", function (event) {
        try {
          let messageData = event.data;
          if (typeof messageData === "string") {
            try {
              messageData = JSON.parse(messageData);
            } catch (parseError) {
              return;
            }
          }
          if (typeof messageData === "object" && messageData?.message) {
            const handler = messageHandlers[messageData.message];
            if (typeof handler === "function") {
              handler();
              window._requestInitPayload = messageData;
            }
          }
        } catch (err) { }
      });
    })();`;
    this.evaluateJavascript(script);
  }

  private gameCloseService() {
    this.events.publish("event:empty:gamedata");
    const obj: any = {
      customer_id: this.gameParams.customer_id,
      session_token: this.gameParams.session_token,
      game_launcher_id: "-1",
    };
    this.webservices.post("games/close-game", obj).subscribe(() => {
    });
    this.events.publish("event:game:close");
    this.gameService.getRecentGames()
    this.events.publish('event:tracker:gameClosed');
    this.events.publish("event:balanceState", { show: true });
    this.resetSessionNetPositions();
  }

  private handleSessionNetPosition(): void {
    const updateSessionStatsPopup = () => {
      if (this.netPositionDisplay) {
        this.injectSessionStatsPopUp(this.netWinOperator, this.netWinAmount);
      }
    };

    updateSessionStatsPopup();
    this.events.subscribe("event:GameStats:Response", (data: string) => {
      console.log("event:GameStats:Response ----", data);

      this.dataToDisplay = data;

      // Determine delay based on config
      const timeout = this.netPositionDelay ? this.netPositionDelay * 1000 : 0;

      // Parse and assign operator and amount
      if (data.startsWith("-")) {
        this.netWinOperator = "-";
        this.netWinAmount = data.substring(1);
      } else {
        this.netWinOperator = "+";
        this.netWinAmount = data;
      }

      // Delay the update if required
      setTimeout(() => {
        updateSessionStatsPopup();
      }, timeout);
    });
  }


  private resetSessionNetPositions() {
    this.netPosition = ["0.00"];
    this.netWinOperator = "+";
    this.netWinAmount = "0.00";
  }


  screenOrientationAdjustmentDebounceTimer:any = null;
  public setupTimeOnlineOverlay() {
    let screenOrientationSubsctiption: Subscription;
    if ("function" === typeof this.evaluateJavascript) {
      var FEATURE_POPUPINTERVAL_MS =
        this.config.getConfig().FEATURE_POPUPINTERVAL_MS;
      if (!FEATURE_POPUPINTERVAL_MS) {
        FEATURE_POPUPINTERVAL_MS = 30 * 60 * 1000;
      }

      if (this.onlineTimeInterval) {
        clearInterval(this.onlineTimeInterval);
        this.onlineTimeInterval = null;
      }

      setTimeout(() => {
        this.injectTimeOnline();
      }, 8000);

      this.onlineTimeInterval = setInterval(() => {
        this.injectTimeOnline();
      }, 60 * 1000);


      screenOrientationSubsctiption = this.screenOrientation
        .onChange()
        .subscribe(() => {
          this.injectSafeArea();
          if (!!this.screenOrientationAdjustmentDebounceTimer) {
            clearTimeout(this.screenOrientationAdjustmentDebounceTimer);
          }

          this.screenOrientationAdjustmentDebounceTimer = setTimeout(() => {
            this.evaluateJavascript(`
                  var div;
                  div = document.getElementById("timeOnline");
                  if (div) {div.remove();}
                  div = document.getElementById( "sessionStats").remove();
                  if (div) {div.remove();}
                `);
            if (this.timeOnlineEnabled) {
              this.injectTimeOnline();
            }
            if (this.netPositionDisplay) {
              this.injectSessionStatsPopUp(this.netWinOperator, this.netWinAmount);
            }
            return;
          }, 500);

          return this.screenOrientationAdjustmentDebounceTimer;
        });
    } else {
      clearInterval(this.onlineTimeInterval);
      this.onlineTimeInterval = null;
    }
  }

  injectSafeArea(): Promise<any> {
    const { top = 0, bottom = 0, left = 0, right = 0 } = this.mobileData?.insets?.insets || {};
    const isLandscape = this.platform.isLandscape();
    const safeAreaStyles = isLandscape
      ? `
          document.body.style.marginTop = "0px";
          document.body.style.marginBottom = "${bottom}px";
          document.body.style.marginLeft = "${left}px";
          document.body.style.marginRight = "${right}px";
          document.body.style.backgroundColor = "#000000";
        `
      : `
          document.body.style.marginTop = "0px";
          document.body.style.marginBottom = "0px";
          document.body.style.marginLeft = "0px";
          document.body.style.marginRight = "0px";
        `;

    const js = `
      if (typeof window !== "undefined") {
        console.log("Applying Safe Area:");
        window.applySafeArea = function() {
          function applySafeArea() {
            ${safeAreaStyles}
          }
          applySafeArea();
        };
        window.applySafeArea();
      }
    `;

    return this.evaluateJavascript(js);
  }


  injectSessionStatsPopUp(operator: string, data: string): Promise<any> {
    const netWinOperator = operator;
    const netWinAmount = data;
    const isPortrait = this.platform.isPortrait();
    const topPx = this.getTopPosition(isPortrait)
    const rightPx = isPortrait ? 0 : 13;

    const fontSize = isPortrait ? "1em" : "0.7em";
    const js = `
      if (typeof window !== "undefined") {
        console.log("session stats: doing injection:");
        window.sessionStatsPopup = function() {
          function displaySessionStats() {
            let div = document.getElementById("sessionStats");
  
            if (!div) {
              const body = document.body;
              div = document.createElement("div");
              div.id = "sessionStats";
              div.style.position = "fixed";
              div.style.padding = "2px 5px";
              div.style.width = "auto";
              div.style.background = "rgba(200,200,200,0.5)";
              div.style.color = "#000";
              div.style.zIndex = "999998";
              div.style.fontFamily = "sans-serif";
              body.appendChild(div);
            }
  
            div.style.top = "${topPx}";
            div.style.right = "${rightPx}px";
            div.style.fontSize = "${fontSize}";
            div.innerHTML = "Net Position: ${netWinOperator}$ ${netWinAmount}";
          }
          displaySessionStats();
        };
        window.sessionStatsPopup();
      }
    `;
    return this.evaluateJavascript(js);
  }


  injectTimeOnline(): Promise<any> {
    const onlineTime = this.timeService.getOnlineTime() || "";
    const isPortrait = this.platform.isPortrait();
    const topPx = this.getTopPosition(isPortrait)
    const leftPx = isPortrait ? 0 : 13;
    const fontSize = isPortrait ? "1em" : "0.7em";
    const js = `
      if (typeof window !== "undefined") {
        console.log("time online: doing injection:");
        window.timeOnlinePopup = function() {
          function displayTimeOnline() {
            let div = document.getElementById("timeOnline");
            
            if (!div) {
              const body = document.body;
              div = document.createElement("div");
              div.id = "timeOnline";
              div.style.position = "fixed";
              div.style.padding = "2px 5px";
              div.style.width = "auto";
              div.style.background = "rgba(200,200,200,0.5)";
              div.style.color = "#000";
              div.style.zIndex = "999998";
              div.style.fontFamily = "sans-serif";
              body.appendChild(div);
            }
            div.style.top = "${topPx}";
            div.style.left = "${leftPx}px";
            div.style.fontSize = "${fontSize}";
            div.innerHTML = "Time online: ${onlineTime}";
          }
          displayTimeOnline();
        };
        window.timeOnlinePopup();
      }
    `;

    return this.evaluateJavascript(js);
  }

  async injectSessionStatsPopUpPopover(e): Promise<any> {
    let time = e.parametersMap.time;
    let wager = e.parametersMap.wagerAmount;
    let statsDialogDisplayTime = this.statsDialogDisplayTime || 15000;
    console.log("session stats: doing injection time:", time);
    let currentTime = "";
    const PIPE_DATE_FORMAT = !!this.config.getConfig().FEATURE_TIME_FORMAT_TO ? this.config.getConfig().FEATURE_TIME_FORMAT_TO : "hh:mm a";
    this.timeService.getServerTime().then((serverTime: any) => {
      if (serverTime.result == 1) { currentTime = formatDate(serverTime.payload.full, PIPE_DATE_FORMAT, "en-US"); }
      const js = `
          if( ("undefined" !== typeof window) ) {
            console.log( "session stats script inside: doing injection:");

            window.sessionStatsPopupPopover = function() {
                console.log('Session Stats Script executed');
                function displaySessionStatsPopover() {
                        var divWrapper;
                        var elemement;
                        if( !document.getElementById( "sessionStatsPopover")) {
                            elem = document.getElementsByTagName( "body")[0];
                            div = document.createElement( "div");
                            div.id = "sessionStatsPopover";
                            div.style.position = "absolute";
                            div.style.top = "50%";
                            div.style.left = "50%";
                            div.style.width = "70%";
                            div.style.transform = "translate(-50%, -50%)";
                            div.style.background = "#fff";
                            div.style.borderRadius = "10px";
                            div.style.padding = "2px 5px";
                            div.style.color = "#000";
                            div.style.zIndex = "999999";
                            div.style.fontFamily = "sans-serif";
                            div.style.fontSize = "0.9rem";
                            elem.appendChild( div);
                        }
                        div = document.getElementById( "sessionStatsPopover");
                        if(div) {
                          try{
                            div.addEventListener("click",function() {document.getElementById("sessionStatsPopover").remove();} )
                          } catch (e) {
                            console.log(e)
                          }
                          div.style.marginLeft = "5px";
                          div.innerHTML = "<div style=' display:block;text-align:center;padding-bottom:15px; font-weight:bold;'>Session Stats<span style='color:black; display:block; width:10px; float:right'>x</span></div><h4>Current Session Stats</h4><p>Current Time: ${currentTime}</p> <p>Time Online: ${time} minute(s)</p>  <p>Wager: $ ${wager}</p>";
                        }
                    }
                    function removePopUpPopover() {
                        setTimeout(function() { document.getElementById("sessionStatsPopover").remove(); }, ${statsDialogDisplayTime});
                    }
                    removePopUpPopover();
                    displaySessionStatsPopover();
                }
                window.sessionStatsPopupPopover();
          }`;
      return this.evaluateJavascript(js);
    });
  }

  private getTopPosition(isPortrait: boolean): string {
    const vendor = this.vendorName?.toLowerCase();
    if (!vendor) return isPortrait ? "0px" : "13px";
    const vendorConfig: Record<string, { portrait?: string; landscape?: string }> = {
      nyx: { portrait: "32px", landscape: "32px" },
      igt: { portrait: "32px", landscape: "32px" },
      playtech: { portrait: "22px", landscape: "22px" }
    };
    const defaults = { portrait: "0px", landscape: "13px" };
    const vendorEntry = vendorConfig[vendor] ?? {};
    return isPortrait ? vendorEntry.portrait ?? defaults.portrait : vendorEntry.landscape ?? defaults.landscape;
  }

  private setWindowRef(windowRef: InAppBrowserObject): void {
    this.windowRef = windowRef as InAppBrowserObject;
  }

  public evaluateJavascript(js: string): Promise<any> {
    if (this.windowRef) {
      return this.windowRef.executeScript({
        code: js,
      });
    } else {
      return Promise.reject("invalid windowRef");
    }
  }

  public onGameClosed(windowRef: InAppBrowserObject, closedCb: () => void) {
    var ticket: number = 1;
    const checkConditions = async () => {
      try {
        const vendor = this.vendorName?.toLowerCase();
        const vendorFlagMap: Record<string, string[]> = {
          igt: ["window.rgs2custReloadGameInProgress", "window.rgs2custReloadGame"],
          internal: ["window._game_has_closed"]
        };
        const flagsToCheck = vendorFlagMap[vendor] || [];
        const results = await Promise.all(flagsToCheck.map(code => windowRef?.executeScript({ code })));
        if (flagsToCheck.length === 0) {
          if (intervalID) clearInterval(intervalID);
        }
        const hasTriggered = results.some(([value]) => value);
        if (hasTriggered) {
          handleClose();
        }
      } catch (error) { console.error("Error checking game close flags:", error); }
    };
    const intervalID = setInterval(checkConditions, 500);
    const handleClose = () => {
      if (--ticket === 0) {
        if(intervalID) clearInterval(intervalID);
        windowRef?.close();
        closedCb();
      }
    };
    windowRef?.on("exit").subscribe(() => {
      console.log("onGameClosed: exit eventd");
      handleClose();
    });
  }
}

declare global {
  interface Window {
    rgs2custReloadGame?: boolean;
    rgs2custReloadGameInProgress?: boolean;
    _requestInitPayload?: any;
    _game_has_closed?: boolean;
  }
}
